/**
 * Wire types for the flat workflow (BPMN) collab patch engine — the client-side
 * twin of the C# server applier. The envelope mirrors sd-patch's ICollabOp;
 * this op vocabulary is a CROSS-LANGUAGE CONTRACT: field names and semantics
 * must stay identical to the C# side, so change them only with both engines.
 *
 * `diffWfDocument` (wf-diff.ts) emits ops; `applyWfOps` (wf-apply.ts) consumes
 * them with skip-not-throw semantics; `invertWfOps` (wf-invert.ts) computes the
 * per-user undo. `wfLwwKeyOf` / `isWfStructural` are the pure helpers the collab
 * session core uses for in-flight last-writer-wins suppression and structural
 * sequencing (same roles as the screen provider's lwwKeyOf / isStructural).
 */

import { IWfBounds, IWfElement, IWfPoint } from "./wf-types";

export type WfCollabOpKind =
  | "wfElementUpsert"
  | "wfElementRemove"
  | "wfGeometrySet"
  | "wfFlowEndpointsSet"
  | "wfAttachSet"
  | "wfContainerSet"
  | "wfPropSet"
  | "wfPropRemove"
  | "wfDocPropSet"
  | "wfDocPropRemove"
  | "wfLaneDerived";

export interface IWfCollabOp {
  opId: string;
  kind: WfCollabOpKind;
  /** Target element id for every element-scoped op kind. */
  elementId?: string;
  /** wfElementUpsert: the full element record (insert-or-replace, verbatim). */
  element?: IWfElement;
  /** wfElementRemove: server-stamped cascade — removed together with the target. */
  cascadedIds?: string[];
  /** wfGeometrySet: only the changed groups travel; an absent group is untouched. */
  bounds?: IWfBounds;
  waypoints?: IWfPoint[];
  labelBounds?: IWfBounds | null;
  /** wfFlowEndpointsSet: both endpoints always travel together. */
  sourceId?: string;
  targetId?: string;
  /** wfAttachSet: the boundary event's new host. */
  hostId?: string;
  /** wfContainerSet: "" = root process, else the target subprocess id. */
  containerId?: string;
  /** wfContainerSet: null = insert first; string = insert after that sibling; absent = append. */
  afterSiblingId?: string | null;
  /**
   * wfPropSet/wfPropRemove: sd-path pointer INTO the element's `semantics`
   * (e.g. "/name", "/dueIn/dueValue"). wfDocPropSet/wfDocPropRemove: pointer
   * into a whitelisted doc root ("/name", "/parameters", "/happyPathDefinition",
   * "/docExtras/description").
   */
  path?: string;
  /** wfPropSet / wfDocPropSet payload. */
  value?: unknown;
  /**
   * wfLaneDerived: the element's new lane membership (null = no lane).
   * SERVER-ONLY ORIGIN — the C# server derives lane membership from geometry
   * (shape center inside lane bounds, root-process shapes only) and appends
   * synthetic wfLaneDerived ops to the accepted batch. A client differ NEVER
   * emits this kind, and the server rejects client-submitted ones with BAD_OP;
   * clients only APPLY it from server broadcasts/acks.
   */
  laneId?: string | null;
}

/**
 * Why an op was skipped. NODE_GONE = target element missing; PARENT_GONE =
 * container missing; CYCLE = containment cycle; TARGET_GONE = endpoint/host
 * missing; BAD_PATH = unresolvable or forbidden pointer; BAD_OP = malformed op.
 */
export type WfApplySkipCode = "NODE_GONE" | "PARENT_GONE" | "CYCLE" | "TARGET_GONE" | "BAD_PATH" | "BAD_OP";

export interface IWfSkippedOp {
  opId: string;
  code: WfApplySkipCode;
}

export interface IWfApplyResult {
  appliedCount: number;
  skippedOps: IWfSkippedOp[];
}

/** Structural ops must apply in global sequence order on every replica. */
export function isWfStructural(op: IWfCollabOp): boolean {
  return op.kind === "wfElementUpsert" || op.kind === "wfElementRemove" || op.kind === "wfContainerSet";
}

/**
 * The LWW conflict key of an op — null for structural ops (those are sequenced,
 * never last-writer-wins suppressed). All geometry groups of one element share
 * one key on purpose: bounds and waypoints racing across clients must converge
 * as a unit.
 */
export function wfLwwKeyOf(op: IWfCollabOp): string | null {
  if (op.kind === "wfGeometrySet") {
    return `geom|${op.elementId}`;
  }
  if (op.kind === "wfFlowEndpointsSet") {
    return `flow|${op.elementId}`;
  }
  if (op.kind === "wfAttachSet") {
    return `attach|${op.elementId}`;
  }
  if (op.kind === "wfPropSet" || op.kind === "wfPropRemove") {
    return `prop|${op.elementId}|${op.path}`;
  }
  if (op.kind === "wfDocPropSet" || op.kind === "wfDocPropRemove") {
    return `doc|${op.path}`;
  }
  if (op.kind === "wfLaneDerived") {
    return `lane|${op.elementId}`;
  }
  return null;
}

const WF_PROP_KEY_PREFIX: string = "prop|";
const WF_DOC_KEY_PREFIX: string = "doc|";

/**
 * The element an op is scoped to: the upsert payload's id (falling back to the
 * envelope elementId), the elementId of every other element-scoped kind, null
 * for doc-scoped ops. Used by the element-scope conflict rule below.
 */
export function wfOpElementIdOf(op: IWfCollabOp): string | null {
  if (op.kind === "wfDocPropSet" || op.kind === "wfDocPropRemove") {
    return null;
  }
  if (op.kind === "wfElementUpsert") {
    const payloadId: string | undefined = op.element?.id;
    if (typeof payloadId === "string" && payloadId.length > 0) {
      return payloadId;
    }
  }
  const elementId: string | undefined = op.elementId;
  if (typeof elementId === "string" && elementId.length > 0) {
    return elementId;
  }
  return null;
}

/** The element scope of an LWW key ("geom|E", "flow|E", "attach|E", "lane|E", "prop|E|path"); null for doc keys. */
export function wfLwwKeyElementIdOf(key: string): string | null {
  if (key.startsWith(WF_DOC_KEY_PREFIX)) {
    return null;
  }
  const firstBar: number = key.indexOf("|");
  if (firstBar < 0) {
    return null;
  }
  if (key.startsWith(WF_PROP_KEY_PREFIX)) {
    const secondBar: number = key.indexOf("|", firstBar + 1);
    if (secondBar < 0) {
      return null;
    }
    return key.slice(firstBar + 1, secondBar);
  }
  return key.slice(firstBar + 1);
}

/**
 * Segment-wise STRICT pointer prefix: "/a" prefixes "/a/b" but never "/ab".
 * Pointer segments are ~-escaped (sd-path: "/" → "~1"), so the "/" boundary
 * check is segment-exact.
 */
function isPointerPrefix(shorter: string, longer: string): boolean {
  return longer.startsWith(`${shorter}/`);
}

/**
 * Prefix-aware LWW key CONFLICT (convergence rule: ancestor/descendant prop
 * paths must suppress each other). Two keys conflict when they are equal, or
 * when both point into the same element's semantics ("prop|E|path") — or the
 * same doc root ("doc|path") — and one path is a segment-wise prefix of the
 * other: a write to "/a" and a write to "/a/b" race over the same subtree,
 * while "/a" vs "/ab" never do. Proven by the wf convergence simulation
 * (ancestor-remove vs descendant-set husk re-creation, T1 finding 3).
 */
export function wfLwwKeysConflict(a: string, b: string): boolean {
  if (a === b) {
    return true;
  }
  if (a.startsWith(WF_PROP_KEY_PREFIX) && b.startsWith(WF_PROP_KEY_PREFIX)) {
    const aPathBar: number = a.indexOf("|", WF_PROP_KEY_PREFIX.length);
    const bPathBar: number = b.indexOf("|", WF_PROP_KEY_PREFIX.length);
    if (aPathBar < 0 || bPathBar < 0) {
      return false;
    }
    if (a.slice(0, aPathBar) !== b.slice(0, bPathBar)) {
      return false; // different elements never conflict
    }
    const aPath: string = a.slice(aPathBar + 1);
    const bPath: string = b.slice(bPathBar + 1);
    return isPointerPrefix(aPath, bPath) || isPointerPrefix(bPath, aPath);
  }
  if (a.startsWith(WF_DOC_KEY_PREFIX) && b.startsWith(WF_DOC_KEY_PREFIX)) {
    const aPath: string = a.slice(WF_DOC_KEY_PREFIX.length);
    const bPath: string = b.slice(WF_DOC_KEY_PREFIX.length);
    return isPointerPrefix(aPath, bPath) || isPointerPrefix(bPath, aPath);
  }
  return false;
}

/**
 * CONSERVATIVE workflow conflict rules that recover by FULL RESYNC from the
 * authority instead of applying the remote op (wf has no resolvedIndex or
 * other server-side position corrections — the sound recovery from an
 * order-sensitive race is the authoritative snapshot). Proven necessary by the
 * wf convergence simulation (T1 findings 1 and 2):
 *
 *  - STRUCTURAL-WHILE-STRUCTURAL-PENDING: a remote structural op arriving while
 *    our OWN structural ops are unacked is a hindsight seq-order violation
 *    lastStructuralSeq cannot see — our op applied locally BEFORE the remote op
 *    it will be sequenced AFTER (same-anchor concurrent order moves transpose).
 *  - ELEMENT-SCOPE UPSERT CONFLICT: a whole-record wfElementUpsert overlaps
 *    EVERY keyed group of its element. A remote upsert over an element we hold
 *    ANY pending op on (or acked at a seq at-or-after the upsert's), and a
 *    remote keyed op on an element we hold a pending upsert for, would silently
 *    clobber one side's fields on this replica only.
 *
 * Screens do NOT use this rule: sd-patch heals the same races with server
 * resolvedIndex corrections and keeps its exact-key LWW semantics.
 */
export function wfRemoteOpForcesResync(
  remoteOp: IWfCollabOp,
  remoteSeq: number,
  pendingOps: readonly IWfCollabOp[],
  ackedKeySeq: ReadonlyMap<string, number>
): boolean {
  if (isWfStructural(remoteOp) && pendingOps.some((pendingOp: IWfCollabOp): boolean => isWfStructural(pendingOp))) {
    return true;
  }
  const remoteElementId: string | null = wfOpElementIdOf(remoteOp);
  if (remoteElementId === null) {
    return false;
  }
  if (remoteOp.kind === "wfElementUpsert") {
    const hasPendingOnElement: boolean = pendingOps.some(
      (pendingOp: IWfCollabOp): boolean => wfOpElementIdOf(pendingOp) === remoteElementId
    );
    if (hasPendingOnElement) {
      return true;
    }
    for (const [ackedKey, ackedSeq] of ackedKeySeq) {
      if (ackedSeq >= remoteSeq && wfLwwKeyElementIdOf(ackedKey) === remoteElementId) {
        return true;
      }
    }
    return false;
  }
  return pendingOps.some(
    (pendingOp: IWfCollabOp): boolean =>
      pendingOp.kind === "wfElementUpsert" && wfOpElementIdOf(pendingOp) === remoteElementId
  );
}

const WF_OP_FALLBACK_PREFIX: string = `wf-${Date.now().toString(36)}-${Math.floor(Math.random() * 0xffffff).toString(36)}`;

let wfOpCounter: number = 0;

/** Fresh op id — crypto UUID when available, a session-unique counter otherwise. */
export function makeWfOpId(): string {
  wfOpCounter += 1;
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${WF_OP_FALLBACK_PREFIX}-${wfOpCounter}`;
}
